import React, { useState, useEffect } from 'react';
import AdminHeader from './AdminHeader';
import './TrainingAdmin.css';

const TrainingAdmin = ({ onNavigate, currentView }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [trainingPaths, setTrainingPaths] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalPaths: 0,
    totalEnrollments: 0,
    activeUsers: 0,
    completionRate: 0
  });

  const API_BASE = '/api/training';

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const sessionId = localStorage.getItem('sessionId');
      const headers = {
        'x-session-id': sessionId,
        'Content-Type': 'application/json'
      };

      if (activeTab === 'overview') {
        // Load overview stats
        const [pathsRes, enrollmentsRes, leaderRes] = await Promise.all([
          fetch(`${API_BASE}/paths`, { headers }),
          fetch(`${API_BASE}/admin/enrollments`, { headers }),
          fetch(`${API_BASE}/leaderboard?limit=10`, { headers })
        ]);

        if (pathsRes.ok) {
          const data = await pathsRes.json();
          setTrainingPaths(data.paths || []);
          setStats(prev => ({ ...prev, totalPaths: data.paths?.length || 0 }));
        }

        if (enrollmentsRes.ok) {
          const data = await enrollmentsRes.json();
          setEnrollments(data.enrollments || []);
          const completed = data.enrollments?.filter(e => e.status === 'completed').length || 0;
          const total = data.enrollments?.length || 0;
          setStats(prev => ({
            ...prev,
            totalEnrollments: total,
            completionRate: total > 0 ? ((completed / total) * 100).toFixed(1) : 0
          }));
        }

        if (leaderRes.ok) {
          const data = await leaderRes.json();
          setLeaderboard(data.leaderboard || []);
          setStats(prev => ({ ...prev, activeUsers: data.leaderboard?.length || 0 }));
        }
      } else if (activeTab === 'paths') {
        const res = await fetch(`${API_BASE}/paths`, { headers });
        if (res.ok) {
          const data = await res.json();
          setTrainingPaths(data.paths || []);
        }
      } else if (activeTab === 'teams') {
        const res = await fetch(`${API_BASE}/teams`, { headers });
        if (res.ok) {
          const data = await res.json();
          setTeams(data.teams || []);
        }
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderOverview = () => (
    <div className="training-overview">
      <section className="stats-grid">
        <div className="stat-card terminal-card">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
            <span className="terminal-title">training.paths</span>
          </div>
          <div className="stat-content">
            <div className="stat-value glow-cyan">{stats.totalPaths}</div>
            <div className="stat-label">Training Paths</div>
          </div>
        </div>
        <div className="stat-card terminal-card">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
            <span className="terminal-title">training.enrollments</span>
          </div>
          <div className="stat-content">
            <div className="stat-value glow-green">{stats.totalEnrollments}</div>
            <div className="stat-label">Total Enrollments</div>
          </div>
        </div>
        <div className="stat-card terminal-card">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
            <span className="terminal-title">training.users</span>
          </div>
          <div className="stat-content">
            <div className="stat-value text-cyber">{stats.activeUsers}</div>
            <div className="stat-label">Active Users</div>
          </div>
        </div>
        <div className="stat-card terminal-card">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
            <span className="terminal-title">training.completion</span>
          </div>
          <div className="stat-content">
            <div className="stat-value text-neon">{stats.completionRate}%</div>
            <div className="stat-label">Completion Rate</div>
          </div>
        </div>
      </section>

      <div className="overview-sections">
        <div className="section">
          <h3>Recent Enrollments</h3>
          <div className="enrollments-table">
            {enrollments.slice(0, 5).map(enrollment => (
              <div key={enrollment._id} className="enrollment-row">
                <div className="enrollment-info">
                  <span className="user-email">{enrollment.userId?.email || 'User'}</span>
                  <span className="path-title">{enrollment.trainingPathId?.title || 'Path'}</span>
                </div>
                <div className="enrollment-meta">
                  <span className={`status ${enrollment.status}`}>{enrollment.status}</span>
                  <span className="progress">{enrollment.progress || 0}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="section">
          <h3>Top Performers</h3>
          <div className="leaderboard-mini">
            {leaderboard.slice(0, 5).map((entry, index) => (
              <div key={entry._id} className="leader-row">
                <span className="rank">#{index + 1}</span>
                <span className="user">{entry.userId?.email || 'User'}</span>
                <span className="points">{entry.totalPoints || 0} pts</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPaths = () => (
    <div className="paths-management">
      <div className="section-header">
        <h2>Training Paths Management</h2>
        <button className="cyber-btn primary">Create New Path</button>
      </div>

      <div className="paths-list">
        {trainingPaths.map(path => (
          <div key={path._id} className="path-item">
            <div className="path-main">
              <h3>{path.title}</h3>
              <p className="description">{path.description}</p>
              <div className="path-details">
                <span className={`difficulty ${path.difficulty}`}>{path.difficulty}</span>
                <span>{path.challenges?.length || 0} challenges</span>
                <span>{path.totalPoints || 0} points</span>
                {path.estimatedHours && <span>{path.estimatedHours}h</span>}
              </div>
            </div>
            <div className="path-actions">
              <button className="cyber-btn secondary">Edit</button>
              <button className="cyber-btn danger">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTeams = () => (
    <div className="teams-management">
      <div className="section-header">
        <h2>Team Challenges</h2>
        <button className="cyber-btn primary">Create Team Challenge</button>
      </div>

      {teams.length === 0 ? (
        <div className="empty-state">
          <p>No team challenges created yet</p>
        </div>
      ) : (
        <div className="teams-list">
          {teams.map(team => (
            <div key={team._id} className="team-item">
              <h3>{team.name}</h3>
              <div className="team-meta">
                <span>{team.members?.length || 0} members</span>
                <span className={`status ${team.status}`}>{team.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="training-admin">
      <AdminHeader currentView={currentView} onNavigate={onNavigate} />

      <div className="admin-content">
        <div className="content-header">
          <h1>Training Management</h1>
          <p>Manage training paths, enrollments, and analytics</p>
        </div>

        <nav className="admin-tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab ${activeTab === 'paths' ? 'active' : ''}`}
            onClick={() => setActiveTab('paths')}
          >
            Training Paths
          </button>
          <button
            className={`tab ${activeTab === 'teams' ? 'active' : ''}`}
            onClick={() => setActiveTab('teams')}
          >
            Team Challenges
          </button>
        </nav>

        <div className="tab-content">
          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <div className="loading-state">
              <div className="cyber-spinner"></div>
              <p>Loading...</p>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && renderOverview()}
              {activeTab === 'paths' && renderPaths()}
              {activeTab === 'teams' && renderTeams()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingAdmin;
